import { twMerge } from 'tailwind-merge';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';
import Tooltip from 'flow/components/ui-components/Tooltip';
import PopoverMenu from 'flow/components/PopoverMenu';
import Menu from 'flow/components/ui-components/Menu';
import MenuItem from 'flow/components/ui-components/MenuItem';
import React, { useEffect, useState } from 'react';
import { TicketKey } from 'flow/types/EntityKeys';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { selectOriginDTOSubtype } from 'flow/selectors/TicketsSelectors';
import useAppContext from 'flow/hooks/useAppContext';
import {
    CHANNEL,
    ChannelValues,
} from 'flow/components/tickets/messageChannels';
import { isMacOs } from 'flow/utils/os-utils-flow';
import { originContactIsPhoneNumber } from 'flow/utils/ticket-channel-utils';

type Props = {
    onClickHandler: () => void;
    multiChannelIsRespected: boolean;
    sendIsEnabled: boolean;
    setSelectedChannel: (channel: ChannelValues) => void;
    ticketKey: TicketKey;
};

interface ChannelType {
    iconKey: IconKey;
    menuTextKey: string;
    channel: ChannelValues;
}

interface ChannelTypes {
    [channel: string]: ChannelType;
}

const CHANNELTYPES: ChannelTypes = Object.freeze({
    WHATSAPP: {
        iconKey: 'whatsapp',
        menuTextKey: 'Send WhatsApp', //TO DO: replace with i18key
        channel: 'whatsapp',
    },
    SMS: {
        iconKey: 'sms',
        menuTextKey: 'QueueSettings.Members.VerifyNumber.SendSMS',
        channel: 'sms',
    },
});

const MultiChannelSendButton = ({
    onClickHandler,
    multiChannelIsRespected,
    setSelectedChannel,
    sendIsEnabled,
    ticketKey,
}: Props) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const [showChannelSelection, setShowChannelSelection] = useState(true);

    const originSubtype = useAppSelector((state) =>
        selectOriginDTOSubtype(state, ticketKey),
    );

    const modifier = isMacOs() ? '⌘' : 'Ctrl';
    const sendTooltipLabel = `${i18next.t('Chat.Tooltip.Send')} (${modifier}+Enter)`;

    const handleChannelSelection = (channel: ChannelValues) => {
        setShowChannelSelection(false);
        setSelectedChannel(channel);
        dispatch(
            segmentTrack(
                `${TICKET_STRINGS.USER_CHANGED_REPLY_CHANNEL} ${channel}`,
                {
                    component: 'Ticket multi channel send button',
                },
            ),
        );
    };

    const getAvailableChannelTypes = () => {
        const availableChannelTypes: ChannelType[] = [];

        if (originSubtype === CHANNEL.WHATSAPP) {
            availableChannelTypes.push(CHANNELTYPES.WHATSAPP);
        }

        if (originContactIsPhoneNumber(originSubtype)) {
            availableChannelTypes.push(CHANNELTYPES.SMS);
        }

        return availableChannelTypes;
    };

    const availableChannelTypes = getAvailableChannelTypes();

    useEffect(() => {
        setShowChannelSelection(true);
    }, [showChannelSelection]);

    return (
        <div className={twMerge('flex h-8 rounded-sm p-0')}>
            <div className="flex h-8 items-center justify-center">
                <Tooltip
                    label={sendTooltipLabel}
                    placement="top"
                    tooltipClassName="h-full"
                >
                    <button
                        aria-label={sendTooltipLabel}
                        onClick={onClickHandler}
                        className={twMerge(
                            'bg-green hover:darken-10 flex h-full w-16 items-center justify-center rounded-sm disabled:bg-gray-300',
                            multiChannelIsRespected &&
                                availableChannelTypes.length > 1
                                ? 'rounded-e-none'
                                : '',
                        )}
                        disabled={!sendIsEnabled}
                    >
                        <div className="flex">
                            <Icon
                                iconKey={'send_message'}
                                size="1.125rem"
                                className={'text-white'}
                            />
                        </div>
                    </button>
                </Tooltip>
                {multiChannelIsRespected && availableChannelTypes.length > 1 ? (
                    <PopoverMenu
                        popoverClassName={twMerge(
                            'flex rounded-s-none ms-0.5 py-1 px-0 leading-none bg-green',
                        )}
                        icon={
                            <Icon
                                iconKey="arrow_down"
                                className={'text-white'}
                            />
                        }
                        placement={'top-end'}
                        closeOnMouseLeave={showChannelSelection}
                        triggerLabel={i18next.t('tickets.reply.choose_channel')}
                    >
                        <Menu className="shadow-base relative mb-2">
                            {availableChannelTypes &&
                                availableChannelTypes.map(
                                    (availableChannel, index) => {
                                        const defaultChannel =
                                            availableChannel.channel ===
                                            originSubtype;
                                        return (
                                            <Item
                                                key={index}
                                                channelType={availableChannel}
                                                handleChannelSelection={
                                                    handleChannelSelection
                                                }
                                                defaultChannel={defaultChannel}
                                            />
                                        );
                                    },
                                )}
                        </Menu>
                    </PopoverMenu>
                ) : null}
            </div>
        </div>
    );
};

type ItemProps = {
    channelType: ChannelType;
    handleChannelSelection: (channel: ChannelValues) => void;
    defaultChannel: boolean;
};

const Item = ({
    channelType,
    handleChannelSelection,
    defaultChannel,
}: ItemProps) => {
    const { i18next } = useAppContext();
    const { channel, iconKey, menuTextKey } = channelType;
    return (
        <MenuItem
            onClick={() => handleChannelSelection(channel)}
            className={'w-72 justify-between'}
        >
            <div className="flex">
                <Icon iconKey={iconKey} className={'me-1'} />
                <span>{i18next.t(menuTextKey)}</span>
            </div>
            {defaultChannel ? (
                <span className="flex items-center justify-center text-xs text-gray-500">
                    {i18next.t('Chat.Tooltip.Default')}
                </span>
            ) : null}
        </MenuItem>
    );
};

export default MultiChannelSendButton;
